import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import {
  APPROVAL_STATUS,
  LEAVE_REQUEST_TYPE,
  REVIEW_COMMENT_MAX,
} from '../constants/leaveApplication.constants';
import type {
  HIERARCHY_FILTER_TYPE,
  REVIEW_DECISION,
} from '../constants/leaveApplication.constants';

export type ApprovalStatus = (typeof APPROVAL_STATUS)[number];
export type LeaveRequestType = (typeof LEAVE_REQUEST_TYPE)[number];
export type HierarchyFilterType =
  (typeof HIERARCHY_FILTER_TYPE)[keyof typeof HIERARCHY_FILTER_TYPE];
export type ReviewDecision = (typeof REVIEW_DECISION)[keyof typeof REVIEW_DECISION];

export const leaveApplicationListItemSchema = z.object({
  id: z.coerce.number(),
  userId: z.coerce.number(),
  userName: z.string(),
  hrEmpId: z.string(),
  amUserId: z.coerce.number().nullable(),
  amName: z.string().nullable(),
  thana: z.string().nullable(),
  district: z.string().nullable(),
  division: z.string().nullable(),
  requestType: z.enum(LEAVE_REQUEST_TYPE),
  leaveTypeId: z.coerce.number().nullable(),
  leaveTypeName: z.string().nullable(),
  leaveTypeCode: z.string().nullable(),
  leaveApplyDate: z.string(),
  dateFrom: z.string(),
  dateTo: z.string(),
  appliedDays: z.coerce.number().nullable(),
  remarks: z.string(),
  status: z.enum(APPROVAL_STATUS),
  reviewedBy: z.coerce.number().nullable(),
  reviewerComment: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});

export const leaveApplicationDetailSchema = leaveApplicationListItemSchema
  .omit({ leaveApplyDate: true })
  .extend({
    createdAt: z.string(),
  });

export const reviewLeaveApplicationSchema = z.object({
  comment: z
    .string()
    .trim()
    .min(1, 'A comment is required')
    .max(REVIEW_COMMENT_MAX, `Comment must be ${REVIEW_COMMENT_MAX} characters or fewer`),
});

export const paginatedLeaveApplicationsSchema = z.object({
  data: z.array(leaveApplicationListItemSchema),
  meta: paginationMetaSchema,
});

export const leaveApplicationSummarySchema = z.object({
  total: z.number(),
  pending: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

export const hierarchyFilterOptionSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
});

export const hierarchyFilterOptionListSchema = z.array(hierarchyFilterOptionSchema);

const leaveTypeUsageSchema = z.object({
  leaveTypeId: z.coerce.number(),
  name: z.string(),
  code: z.string(),
  days: z.coerce.number(),
});

const leaveUsageSchema = z.object({
  byType: z.array(leaveTypeUsageSchema),
  attendanceClaimDays: z.coerce.number(),
  rejectedRequests: z.coerce.number(),
});

export const leaveUsageSummarySchema = z.object({
  year: z.coerce.number(),
  month: z.coerce.number(),
  yearly: leaveUsageSchema,
  monthly: leaveUsageSchema,
});

export type LeaveApplicationListItem = z.infer<typeof leaveApplicationListItemSchema>;
export type LeaveApplicationDetail = z.infer<typeof leaveApplicationDetailSchema>;
export type ReviewLeaveApplicationPayload = z.infer<typeof reviewLeaveApplicationSchema>;
export type PaginatedLeaveApplications = z.infer<typeof paginatedLeaveApplicationsSchema>;
export type LeaveApplicationSummary = z.infer<typeof leaveApplicationSummarySchema>;
export type HierarchyFilterOption = z.infer<typeof hierarchyFilterOptionSchema>;
export type LeaveUsage = z.infer<typeof leaveUsageSchema>;
export type LeaveUsageSummary = z.infer<typeof leaveUsageSummarySchema>;

export interface LeaveUsageSummaryParams {
  year?: number | undefined;
  month?: number | undefined;
}

export interface LeaveApplicationListParams {
  status?: ApprovalStatus | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  requestType?: LeaveRequestType | undefined;
  leaveTypeId?: number | undefined;
  amUserId?: number | undefined;
  zmUserId?: number | undefined;
  divisionId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
  search?: string | undefined;
}

export type LeaveApplicationSummaryParams = LeaveApplicationListParams;

export type LeaveApplicationCountsBySubmitterParams = Omit<
  LeaveApplicationListParams,
  'amUserId' | 'zmUserId'
>;
